import { useEffect, useMemo, useRef, useState } from 'react';
import { Control, FormProvider, useForm, UseFormRegister } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCreateSegment } from '@/api/segments/useCreateSegment';
import { usePrecalculateSegment } from '@/api/segments/usePrecalculateSegment';
import { useAppSelector } from '@/app/hooks';
import Banner from '@/component-library/primitives/Banner/Banner';
import { Button } from '@/component-library/primitives/Button/Button';
import ChevronNewIcon from '@/component-library/primitives/Icons/ChevronNewIcon/ChevronNewIcon';
import Loader from '@/component-library/primitives/Loader/Loader';
import Stepper from '@/component-library/primitives/Stepper/Stepper';
import { Textarea } from '@/component-library/primitives/Textarea/Textarea';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { Input } from '@/component-library/widgets/Input/Input';
import { QuickFilterCriteria } from '@/component-library/widgets/QuickFilter/QuickFilter';
import { useTracking } from '@/hooks/useTracking';
import { CreateSegmentDetails, SegmentRule } from '@/models/Segments';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { AppRoutes } from '@/utils/routes/router';

import SegmentFilterForm from './SegmentFilterForm';

const CreateSegment = () => {
  const createSegmentMutation = useCreateSegment();
  const precalculateSegmentMutation = usePrecalculateSegment();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Type instantiation depth issue with i18next
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const workspace = useAppSelector(selectCurrentWorkspace);
  const [activeStep, setActiveStep] = useState(0);
  const divWhereToScroll = useRef<HTMLDivElement>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [createdSegmentId, setCreatedSegmentId] = useState(null);
  const [filterCriteria, setFilterCriteria] = useState<QuickFilterCriteria>({});

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Type instantiation depth issue with i18next
  const steps = [
    t('CreateSegmentPage.createSegmentStep'),
    t('CreateSegmentPage.setUpStep'),
    t('CreateSegmentPage.previewStep'),
  ];

  useEffect(() => {
    let timerBeforeNavigation: NodeJS.Timeout;
    if (toastInfo.toastType === 'success' && !createdSegmentId) {
      timerBeforeNavigation = setTimeout(() => navigate(AppRoutes.segments), 2000);
    } else if (toastInfo.toastType === 'success' && createdSegmentId) {
      timerBeforeNavigation = setTimeout(
        () => navigate(generatePath(AppRoutes.segmentOverview, { id: createdSegmentId })),
        2000,
      );
    }
    return () => {
      clearTimeout(timerBeforeNavigation);
    };
  }, [navigate, toastInfo]);

  const schema = useMemo(() => {
    return [
      yup.object({
        name: yup
          .string()
          .trim()
          .required(t('CreateSegmentPage.nameRequired'))
          .min(1, t('CreateSegmentPage.nameTooShort', { minLength: 1 }))
          .max(50, t('CreateSegmentPage.nameTooLong', { maxLength: 50 })),
        description: yup.string().trim(),
      }),
      yup.object({}), // Step 1 validation is handled manually in onNext
      yup.object({}),
    ];
  }, [t]);

  const currentValidationSchema = schema[activeStep];

  const methods = useForm<CreateSegmentDetails>({
    shouldUnregister: false,
    resolver: yupResolver(currentValidationSchema),
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    trigger,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = methods;

  // Convert QuickFilterCriteria to SegmentRule format
  const criteriaToRule = (criteria: QuickFilterCriteria): SegmentRule => {
    const conditions: any[] = [];

    // Risk Level condition
    if (criteria.riskLevel) {
      const riskScoreRanges: { [key: string]: { from: number; to: number } } = {
        high: { from: 60, to: 100 },
        medium: { from: 30, to: 59 },
        low: { from: 0, to: 29 },
      };
      const range = riskScoreRanges[criteria.riskLevel];
      conditions.push({
        fact: 'attribute',
        operator: 'greaterThanInclusive',
        value: range.from,
        path: '$.value',
        params: { key: 'riskScore' },
      });
      conditions.push({
        fact: 'attribute',
        operator: 'lessThanInclusive',
        value: range.to,
        path: '$.value',
        params: { key: 'riskScore' },
      });
    }

    // Risk Score Range conditions
    if (criteria.riskScoreFrom !== null && criteria.riskScoreFrom !== undefined) {
      conditions.push({
        fact: 'attribute',
        operator: 'greaterThanInclusive',
        value: criteria.riskScoreFrom,
        path: '$.value',
        params: { key: 'riskScore' },
      });
    }
    if (criteria.riskScoreTo !== null && criteria.riskScoreTo !== undefined) {
      conditions.push({
        fact: 'attribute',
        operator: 'lessThanInclusive',
        value: criteria.riskScoreTo,
        path: '$.value',
        params: { key: 'riskScore' },
      });
    }

    // Email Domain condition
    if (criteria.emailDomain) {
      conditions.push({
        fact: 'email',
        operator: 'contains',
        value: `@${criteria.emailDomain}`,
      });
    }

    // Status condition
    if (criteria.status) {
      conditions.push({
        fact: 'attribute',
        operator: 'equal',
        value: criteria.status,
        path: '$.value',
        params: { key: 'status' },
      });
    }

    // Signal Action Type condition
    if (criteria.signalActionType) {
      conditions.push({
        fact: 'attribute',
        operator: 'contains',
        value: criteria.signalActionType,
        path: '$.value',
        params: { key: 'recommendedActions' },
      });
    }

    // If no conditions, return empty rule (should be validated before)
    if (conditions.length === 0) {
      return {
        rule: {
          name: getValues('name'),
          attributes: [],
          decisions: [
            {
              conditions: {
                all: [],
              },
              event: {
                type: 'PartOfSegment',
                params: {},
              },
            },
          ],
        },
      };
    }

    // Get unique attribute names
    const attributeNames = new Set<string>();
    if (criteria.riskLevel || criteria.riskScoreFrom !== null || criteria.riskScoreTo !== null) {
      attributeNames.add('riskScore');
    }
    if (criteria.emailDomain) {
      attributeNames.add('email');
    }
    if (criteria.status) {
      attributeNames.add('status');
    }
    if (criteria.signalActionType) {
      attributeNames.add('recommendedActions');
    }

    const attributes = Array.from(attributeNames).map((name) => ({
      name,
      type: name === 'riskScore' ? 'number' : name === 'email' ? 'string' : 'string',
    }));

    return {
      rule: {
        name: getValues('name'),
        attributes,
        decisions: [
          {
            conditions: {
              all: conditions, // All conditions must be met (AND logic)
            },
            event: {
              type: 'PartOfSegment',
              params: {},
            },
          },
        ],
      },
    };
  };

  const onBack = () => {
    activeStep === 0 ? navigate(AppRoutes.segments) : setActiveStep(activeStep - 1);
    divWhereToScroll.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onNext = async () => {
    const isStepValid = await trigger();
    if (isStepValid) {
      if (activeStep === 1) {
        // Validate that at least one filter criteria is set
        const hasCriteria = !!(
          filterCriteria.riskLevel ||
          filterCriteria.emailDomain ||
          filterCriteria.riskScoreFrom !== null ||
          filterCriteria.riskScoreTo !== null ||
          filterCriteria.status ||
          filterCriteria.signalActionType
        );

        if (!hasCriteria) {
          setToastInfo({ message: 'Please set at least one filter criteria', toastType: 'error' });
          setOpen(true);
          return;
        }

        const formattedRuleObject = criteriaToRule(filterCriteria);
        precalculateSegmentMutation.mutate(formattedRuleObject, {
          onSuccess: (res: any) => {
            setMemberCount(res.data.length);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
          },
          onError: () => {
            console.log('failed to precalculate members');
            setMemberCount(0);
          },
        });
      } else if (activeStep === 0) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
    divWhereToScroll.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onSubmit = async (data: CreateSegmentDetails) => {
    const ruleObject = criteriaToRule(filterCriteria);
    const sendData = {
      name: data.name,
      description: data.description,
      workspaceId: workspace?.id,
      segmentType: 'data-driven', // All segments are now data-driven (filter-based)
      rule: JSON.stringify(ruleObject),
    };

    createSegmentMutation.mutate(sendData as CreateSegmentDetails, {
      onSuccess: () => {
        trackEvent('segment_created', {
          segmentType: 'data-driven',
          hasDescription: !!data.description,
        });
        setToastInfo({ message: t('CreateSegmentPage.toastSuccess'), toastType: 'success' });
        setOpen(true);
      },
      onError: () => {
        setToastInfo({ message: t('CreateSegmentPage.toastError'), toastType: 'error' });
        setOpen(true);
      },
    });
  };

  const onSubmitWithRedirection = async (data: CreateSegmentDetails) => {
    const ruleObject = criteriaToRule(filterCriteria);
    const sendData = {
      name: data.name,
      description: data.description,
      workspaceId: workspace?.id,
      segmentType: 'data-driven', // All segments are now data-driven (filter-based)
      rule: JSON.stringify(ruleObject),
    };

    createSegmentMutation.mutate(sendData as CreateSegmentDetails, {
      onSuccess: (res: any) => {
        trackEvent('segment_created', {
          segmentType: 'data-driven',
          hasDescription: !!data.description,
        });
        setToastInfo({ message: t('CreateSegmentPage.toastSuccess'), toastType: 'success' });
        setOpen(true);
        setCreatedSegmentId(res.data.segmentId);
      },
      onError: () => {
        setToastInfo({ message: t('CreateSegmentPage.toastError'), toastType: 'error' });
        setOpen(true);
      },
    });
  };

  return (
    <div className="relative mb-[350px]">
      {createSegmentMutation.isLoading && (
        <div className="absolute w-full h-full z-50">
          <Loader />
        </div>
      )}
      <div className="flex flex-col items-center mt-4">
        <div className="flex min-w-[40rem] items-start -ml-6" ref={divWhereToScroll}>
          <Stepper steps={steps} activeStep={activeStep} />
        </div>
        <div className="w-[40rem]">
          <FormProvider {...methods}>
            <form className="space-y-6 min-w-[40rem] w-1/2">
              {/* Create Segment step */}
              {activeStep === 0 && (
                <div>
                  <div className="relative mb-7">
                    <h1 className="text-custom-textBlack font-medium text-xl">
                      {t('CreateSegmentPage.createSegmentStep')}
                    </h1>
                  </div>
                  <section className="space-y-3">
                    <h2 className="text-xs font-semibold text-primary-400 mb-3">
                      {t('CreateSegmentPage.nameYourSegmentLabel')}
                    </h2>
                    <Input
                      placeholder={t('CreateSegmentPage.segmentNamePlaceholder')}
                      {...register('name')}
                      error={errors.name?.message as string}
                      label={t('CreateSegmentPage.segmentNameLabel')}
                      data-cy="segmentName"
                    />
                    <Textarea
                      {...register('description')}
                      placeholder={t('CreateSegmentPage.descriptionPlaceholder')}
                      error={errors.description?.message}
                      label={t('CreateSegmentPage.descriptionLabel')}
                      optional={true}
                    />
                  </section>
                  <div className="flex items-center justify-center fixed bottom-0 z-50 bg-white backdrop-blur-[8px] overflow-hidden w-screenWithoutSideMenu left-[21rem] h-24 border-t border-gray-200">
                    <div className="flex items-center justify-between min-w-[40rem]">
                      <Button variant="cancel" onClick={() => navigate(AppRoutes.segments)}>
                        {t('CreateSegmentPage.linkCancel')}
                      </Button>
                      <div className="flex flex-row gap-2">
                        <Button
                          type="button"
                          className="group pl-4 !w-[8.125rem]"
                          data-cy="nextButton"
                          onClick={onNext}
                          iconRight={
                            <span className="group-hover:[&>*_path]:stroke-gray-900 group-active:[&>*_path]:stroke-white">
                              <ChevronNewIcon type="right" color="#FFFFFF" width="20" height="20" />
                            </span>
                          }
                        >
                          {t('CreateSegmentPage.buttonNext')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Set up Segment Filters step  */}
              {activeStep === 1 && (
                <div>
                  <div className="mb-5">
                    <h1 className="text-custom-textBlack font-medium text-xl mb-2">
                      {t('CreateSegmentPage.setUpStep')}
                    </h1>
                    <span className="text-gray-300 text-sm">
                      Define filter criteria to determine which users belong to this segment. All selected criteria will
                      be combined with AND logic.
                    </span>
                  </div>
                  <section>
                    <SegmentFilterForm criteria={filterCriteria} onCriteriaChange={setFilterCriteria} />
                  </section>
                  <div className="flex items-center justify-center fixed bottom-0 z-50 bg-white backdrop-blur-[8px] overflow-hidden w-screenWithoutSideMenu left-[21rem] h-24 border-t border-gray-200">
                    <div className="flex items-center justify-between min-w-[40rem]">
                      <Button variant="cancel" onClick={() => navigate(AppRoutes.segments)}>
                        {t('CreateSegmentPage.linkCancel')}
                      </Button>
                      <div className="flex flex-row gap-2">
                        <Button
                          type="button"
                          variant="previous"
                          className="pr-4 !w-[8.125rem]"
                          onClick={onBack}
                          iconLeft={<ChevronNewIcon type="left" color="#F5F7FC" width="20" height="20" />}
                        >
                          {t('CreateSegmentPage.buttonPrevious')}
                        </Button>
                        <Button
                          type="button"
                          className="group pl-4 !w-[8.125rem]"
                          data-cy="nextButton"
                          onClick={onNext}
                          isLoading={precalculateSegmentMutation.isLoading}
                          iconRight={
                            <span className="group-hover:[&>*_path]:stroke-gray-900 group-active:[&>*_path]:stroke-white">
                              <ChevronNewIcon type="right" color="#FFFFFF" width="20" height="20" />
                            </span>
                          }
                        >
                          {t('CreateSegmentPage.buttonNext')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Preview step */}
              {activeStep === 2 && (
                <div>
                  <div className="mb-9">
                    <h1 className="text-white font-medium text-xl mb-3">{t('CreateSegmentPage.previewStep')}</h1>
                    <div className="mt-8">
                      <Banner variant="info">
                        <h3 className="text-sm text-white font-normal tacking-[0.28px]">
                          <Trans
                            i18nKey="CreateSegmentPage.peopleBelongingToSegmentLabel"
                            values={{
                              peopleNumber: memberCount,
                            }}
                          />
                        </h3>
                        <p className="text-xs font-normal text-gray-400 tracking-[0.24px]">
                          {t('CreateSegmentPage.peopleBelongingToSegmentDescription')}
                        </p>
                      </Banner>
                    </div>
                  </div>
                  <h2 className="mb-4 text-xs font-semibold text-primary-400">Filter Criteria Summary</h2>
                  <section>
                    <div className="bg-primary-0 rounded-lg p-4 space-y-2">
                      {filterCriteria.riskLevel && (
                        <div className="text-sm text-custom-textBlack">
                          <span className="font-medium">Risk Level:</span> {filterCriteria.riskLevel}
                        </div>
                      )}
                      {filterCriteria.emailDomain && (
                        <div className="text-sm text-custom-textBlack">
                          <span className="font-medium">Email Domain:</span> @{filterCriteria.emailDomain}
                        </div>
                      )}
                      {(filterCriteria.riskScoreFrom !== null || filterCriteria.riskScoreTo !== null) && (
                        <div className="text-sm text-custom-textBlack">
                          <span className="font-medium">Risk Score Range:</span>{' '}
                          {filterCriteria.riskScoreFrom !== null ? filterCriteria.riskScoreFrom : 0} -{' '}
                          {filterCriteria.riskScoreTo !== null ? filterCriteria.riskScoreTo : 100}
                        </div>
                      )}
                      {filterCriteria.status && (
                        <div className="text-sm text-custom-textBlack">
                          <span className="font-medium">Status:</span> {filterCriteria.status}
                        </div>
                      )}
                      {filterCriteria.signalActionType && (
                        <div className="text-sm text-custom-textBlack">
                          <span className="font-medium">Action Type:</span> {filterCriteria.signalActionType}
                        </div>
                      )}
                    </div>
                  </section>
                  <div className="flex items-center justify-center fixed bottom-0 z-50 bg-white backdrop-blur-[8px] overflow-hidden w-screenWithoutSideMenu left-[21rem] h-24 border-t border-gray-200">
                    <div className="flex items-center justify-between min-w-[40rem]">
                      <Button variant="cancel" onClick={() => navigate(AppRoutes.segments)}>
                        {t('CreateSegmentPage.linkCancel')}
                      </Button>
                      <div className="flex flex-row gap-2">
                        <Button type="button" variant="previous" className="!min-w-[48px] !w-12" onClick={onBack}>
                          <ChevronNewIcon type="left" color="#F5F7FC" width="20" height="20" />
                        </Button>
                        <Button
                          variant="secondary"
                          className="px-4 !w-52"
                          onClick={handleSubmit(onSubmitWithRedirection)}
                          isLoading={createSegmentMutation.isLoading || createSegmentMutation.isSuccess}
                        >
                          {t('CreateSegmentPage.buttonSaveAndGoToOverview')}
                        </Button>
                        <Button
                          className="px-4 !w-40"
                          data-cy="saveSegmentButton"
                          onClick={handleSubmit(onSubmit)}
                          isLoading={createSegmentMutation.isLoading || createSegmentMutation.isSuccess}
                        >
                          {t('CreateSegmentPage.buttonSaveAndQuit')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </FormProvider>
        </div>
        <ProvideToasts>
          <Toast
            isOpen={open}
            setOpen={setOpen}
            message={toastInfo.message}
            toastType={toastInfo.toastType as ToastType}
          />
        </ProvideToasts>
      </div>
    </div>
  );
};

export default CreateSegment;
